import type { IBackend } from '@/api/BackendApi';
import type { RecipeTemplateDto } from '@/api/ApiClient';
import { ActivityTypeDto } from '@/api/ApiClient';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import { log } from '@/lib/utils/logger';

/** Thin-film prep/post templates when no technique is set on activity or sample. */
export const SUBSTRATE_TECHNIQUE = 'Substrate';

export function isPrepOrPostActivityType(activityType?: string): boolean {
  return activityType === ActivityTypeDto.Preparation || activityType === ActivityTypeDto.PostTreatment;
}

/**
 * Technique keys to try when resolving a prep recipe template.
 * Activity technique first, then parent sample technique (bulk prep stores technique on sample).
 */
export function prepTemplateTechniqueCandidates(
  activityTechnique?: string | null,
  sampleTechniqueId?: string | null
): string[] {
  const candidates: string[] = [];
  const add = (value?: string | null) => {
    const trimmed = value?.trim();
    if (trimmed && !candidates.includes(trimmed)) {
      candidates.push(trimmed);
    }
  };
  add(activityTechnique);
  add(sampleTechniqueId);
  return candidates;
}

/**
 * Primary technique for template list APIs (availableTemplates).
 * Prefers activity, then sample, then Substrate for prep/post without instrument.
 */
export function primaryRecipeTemplateTechnique(
  activityType: string | undefined,
  activityTechnique: string | undefined,
  sampleTechniqueId: string | undefined,
  instrument: string | undefined
): string | null {
  const candidates = prepTemplateTechniqueCandidates(activityTechnique, sampleTechniqueId);
  if (candidates.length > 0) {
    return candidates[0];
  }
  if (isPrepOrPostActivityType(activityType) && !instrument?.trim()) {
    return SUBSTRATE_TECHNIQUE;
  }
  return null;
}

/**
 * Fetch a recipe template, trying activity technique then sample technique when needed.
 * For prep/post with no candidates, falls back to Substrate (thin-film templates).
 */
export async function getRecipeTemplateWithTechniqueFallback(
  backend: IBackend,
  params: {
    activityType?: string;
    activityTechnique?: string;
    sampleTechniqueId?: string;
    instrument?: string;
    templateName: string;
  }
): Promise<RecipeTemplateDto | null> {
  const { activityType, activityTechnique, sampleTechniqueId, instrument, templateName } = params;
  const step = toRecipeTemplateStep(activityType) ?? null;
  const instrumentArg = instrument?.trim() || null;

  const candidates = prepTemplateTechniqueCandidates(activityTechnique, sampleTechniqueId);
  if (isPrepOrPostActivityType(activityType) && !instrumentArg && candidates.length === 0) {
    candidates.push(SUBSTRATE_TECHNIQUE);
  }

  for (const technique of candidates) {
    try {
      const template = await backend.recipeTemplate.get(step, technique, instrumentArg, templateName);
      if (template) {
        return template;
      }
    } catch (err) {
      log.debug(`[recipeTemplateLookup] Template "${templateName}" not found for technique "${technique}"`, err);
    }
  }

  return null;
}
